import React, { useEffect, useState } from 'react';

import LoadingAnimation from 'components/loading-animation';
import {
  IGenerateVrPaymentCheckoutLinkMutation,
  useGenerateVrPaymentCheckoutLinkMutation,
} from 'generated/rbi-graphql';
import logger from 'utils/logger';

import { OutcomeResult } from '../types';

import { HostedPaymentContainer } from './hosted-payment-form.styled';

export interface IHostedPaymentFormProps {
  onResult: (outcome: OutcomeResult) => void;
  rbiOrderId: string;
}

export const HostedPaymentForm: React.FC<IHostedPaymentFormProps> = ({ rbiOrderId, onResult }) => {
  const [paymentLink, setPaymentLink] = useState('');
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const handleVrPaymentLinkCompleted = (result: IGenerateVrPaymentCheckoutLinkMutation) =>
    setPaymentLink(result.generateVrPaymentCheckoutLink.link);

  const [generateVrPaymentLink] = useGenerateVrPaymentCheckoutLinkMutation({
    onCompleted: handleVrPaymentLinkCompleted,
    onError: error => {
      logger.error({
        message: 'VR Payment: Non-vaulted CC integration threw an error',
        error,
      });

      throw error;
    },
  });

  useEffect(() => {
    if (rbiOrderId) {
      generateVrPaymentLink({
        variables: { rbiOrderId },
      });
    }
  }, [rbiOrderId, generateVrPaymentLink]);

  useEffect(() => {
    if (!paymentLink) {
      return undefined;
    }

    function listener(event: MessageEvent) {
      const origin = new URL(event.origin);
      if (!origin.hostname.endsWith('.rbictg.com')) {
        return;
      }
      if (event.data.type !== 'payment-outcome') {
        return;
      }

      const body = event.data.body as OutcomeResult;

      onResult(body);
    }

    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [paymentLink, onResult]);

  return (
    <HostedPaymentContainer>
      {!paymentLink && !iframeLoaded && <LoadingAnimation fillColor={Styles.color.primary} />}
      {paymentLink && (
        <iframe
          onLoad={() => setIframeLoaded(true)}
          data-testid="hosted-payment-form"
          title="Payment Form"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            visibility: iframeLoaded ? 'visible' : 'hidden',
          }}
          src={paymentLink}
        ></iframe>
      )}
    </HostedPaymentContainer>
  );
};
